import { Dialog, Typography } from "@mui/material";
import { Box } from "@mui/system";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalCard, { ModalRow } from "../../components/modal/ModalCard";
import CollapsibleTable, {
    IRow,
} from "../../components/table/collapse/CollapsibleTable";
import { ILocalDisplay } from "../../models/requests/local/localDisplay";
import { IHBScanFloor, IProcessedScan, IScan } from "../../models/stat";
import { routes } from "../../routes";
import localService from "../../services/local/LocalService";
import DateUtils from "../../utils/dateUtils";
import UniqueKey from "../../utils/uniqueKey";

interface IScanDisplay extends Omit<IScan, "date"> {
    minutes: string;
    date: string;
    endDate: string;
}

function CreateScanDetails(scans: IProcessedScan[]): IScanDisplay[] {
    return scans.map((scan) => {
        const date = moment.utc(scan.start.date).local().format("LLL");
        const endDate =
            scan.minutes >= 1
                ? moment
                    .utc(scan.start.date)
                    .local()
                    .add(scan.minutes, "minute")
                    .format("LLL")
                : "-".repeat(date.length);
        return {
            ...scan.start,
            minutes: DateUtils.displayMinutes(scan.minutes),
            date: date,
            endDate: endDate,
        };
    });
}

function AggregateScans(scans: IProcessedScan[]) {
    return scans?.reduce((acc, scan) => {
        if (acc.has(scan.start.userId)) {
            acc.get(scan.start.userId)?.push(scan);
            return acc;
        }
        acc.set(scan.start.userId, [scan]);
        return acc;
    }, new Map<number, IProcessedScan[]>());
}

interface LocalDialogProps {
    selectedLocalId: number | undefined;
    setSelectedLocalId: Dispatch<SetStateAction<number | undefined>>;
    hbScanFloors: IHBScanFloor[];
}

const LocalDialog = ({
    hbScanFloors,
    selectedLocalId,
    setSelectedLocalId,
}: LocalDialogProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localScans, setLocalScans] = useState<IRow<IScanDisplay>[]>([]);
    const [totalScan, setTotalScan] = useState<string>("");
    const [localName, setLocalName] = useState<string>("");
    const [local, setLocal] = useState<ILocalDisplay>();
    const { t } = useTranslation();

    useEffect(() => {
        if (!selectedLocalId) return;
        localService.GetLocalsBuildingsByCompany().then((locals) => {
            const localDetail = locals.localDetails.find((l) => l.local.id === selectedLocalId);
            if (localDetail) {
                const localRequest: ILocalDisplay = {
                    id: localDetail.local.id,
                    buildingId: localDetail.building.id,
                    floorId: localDetail.floor.id,
                    name: localDetail.local.name,
                    profileId: localDetail.profile.id,
                    codes: localDetail.codes || [],
                    alarmCode: localDetail.local.alarmCode || "",
                    cleaningSchedule: localDetail.cleaningSchedule,
                    specification: localDetail.local.specification || "",
                    routes: localDetail?.routes || [],
                    isLocked: localDetail.local.isLocked || false,
                    buildings: locals.buildings || [],
                    tagMacAddress: localDetail.local.tagMacAddress || "",
                    buildingName: localDetail.building.name,
                    floorName: localDetail.floor.name,
                };
                setLocal(localRequest);
            }
        });

        const room = hbScanFloors
            .flatMap((floor) => floor.rooms)
            .find((r) => r.roomId === selectedLocalId);
        if (!room) return;
        const scans = room.scans;

        const localScans = AggregateScans(scans);

        const rows: IRow<IScanDisplay>[] = Array.from(localScans.entries()).map(
            ([userId, localScans]) => {
                const nMinutes = localScans.reduce(
                    (acc, scan) => (acc += scan.minutes),
                    0
                );
                const sortedScans = localScans.sort(
                    (a, b) => moment(b.start.date).unix() - moment(a.start.date).unix()
                );
                const subRows = CreateScanDetails(sortedScans);
                return {
                    id: UniqueKey.generateId({ length: 8, prefix: userId }),
                    subRowData: subRows,
                    data: {
                        ...localScans[0].start,
                        minutes: DateUtils.displayMinutes(nMinutes),
                        date: "",
                        endDate: "",
                    },
                };
            }
        );

        setLocalScans(rows);
        setIsOpen(rows.length >= 0);
        setTotalScan(
            DateUtils.displayMinutes(
                scans.reduce((acc, scan) => (acc += scan.minutes), 0)
            )
        );
        setLocalName(room.roomName);
    }, [selectedLocalId, hbScanFloors]);

    if (!isOpen) return <></>;

    const close = () => {
        setIsOpen(false);
        setSelectedLocalId(undefined);
    };

    return (
        <Dialog open={isOpen} onClose={close}>
            <ModalCard
                title={localName}
                titleLink={`${routes.tableRoute.locals}/${routes.tableRoute.localEdit}`}
                titleLinkState={{ localData: local }}
                isTitleLinkExternal={true}
                label={t("header.room")}
                closeCallback={close}
            >
                <ModalCard.Main isDivider={false}>
                    <Box
                        sx={{
                            maxHeight: "50vh",
                            minWidth: "500px",
                            overflow: "scroll",
                        }}
                    >
                        <CollapsibleTable
                            elevation={0}
                            rows={localScans}
                            columns={[
                                { field: "username", headerName: "" },
                                { field: "minutes", headerName: "" },
                            ]}
                            subColumns={[
                                {
                                    field: "date",
                                    headerName: t("header.startDate"),
                                },
                                {
                                    field: "endDate",
                                    headerName: t("header.endDate"),
                                },
                                {
                                    field: "minutes",
                                    headerName: t("header.time"),
                                },
                            ]}
                        />
                        <ModalRow sx={{ marginTop: 2 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ marginRight: "auto" }}
                            >
                                {t("header.totalTime")}
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                sx={{ marginLeft: "auto", marginRight: 2 }}
                            >
                                {totalScan}
                            </Typography>
                        </ModalRow>
                    </Box>
                </ModalCard.Main>
            </ModalCard>
        </Dialog>
    );
};

export default LocalDialog;
